import type {ChatStreamMessage} from '../contexts/ChatStreamContext'
import type {LogEntry} from '../types/session'
import type {CustomAgentGroupLogEntry} from './group-agent-logs'

/**
 * Extracts the content string from either a LogEntry or ChatStreamMessage.
 */
export function getEntryContent(entry: LogEntry | ChatStreamMessage): string {
  if ('choices' in entry && Array.isArray(entry.choices)) {
    return (entry.choices as Array<{delta?: {content?: string}}>).map(choice => choice.delta?.content ?? '').join('')
  }

  if ('content' in entry && typeof entry.content === 'string') {
    return entry.content
  }

  return ''
}

/**
 * Extracts a comparable content length from either a LogEntry or ChatStreamMessage.
 */
export function getEntryContentLength(entry: LogEntry | ChatStreamMessage): number {
  return getEntryContent(entry).length
}

/**
 * Finds the first offset in logs where the chat history aligns via
 * prefix matching. At each candidate offset, every non-empty pair in
 * the overlap region must satisfy: one content string is a prefix of
 * the other. Empty-content entries are skipped. Returns the first
 * matching offset, or null to signal that chat should be appended.
 */
function findAlignmentOffset(logs: CustomAgentGroupLogEntry[], chat: ChatStreamMessage[]): number | null {
  for (let k = 0; k < logs.length; k++) {
    const overlapEnd = Math.min(logs.length - k, chat.length)
    let allMatch = true
    let anyMatch = false

    for (let i = 0; i < overlapEnd; i++) {
      const logEntry = logs[k + i]
      const chatEntry = chat[i]
      if (!logEntry || !chatEntry) continue

      const logContent = getEntryContent(logEntry as LogEntry)
      const chatContent = getEntryContent(chatEntry)

      if (logContent.length === 0 || chatContent.length === 0) continue

      if (logContent.startsWith(chatContent) || chatContent.startsWith(logContent)) {
        anyMatch = true
      } else {
        allMatch = false
        break
      }
    }

    if (allMatch && anyMatch) return k
  }

  return null
}

/**
 * Merges preprocessed log entries with streaming chat history by aligning
 * the two arrays on partial content matches.
 *
 * The algorithm finds the latest position in the log entries where the chat
 * history overlaps (both arrays have entries with content at the same
 * relative position). Entries before that point are kept as-is. From that
 * point onward the two lists are merged position-by-position, preferring
 * whichever entry has the longer content at each index.
 *
 * When no overlap is detected the chat history is treated as newer content
 * and is appended after the log entries.
 */
export function mergeLogsWithChatHistory(
  preprocessedLogEntries: CustomAgentGroupLogEntry[],
  chatMessageHistory: ChatStreamMessage[],
): CustomAgentGroupLogEntry[] {
  if (chatMessageHistory.length === 0) {
    return preprocessedLogEntries
  }

  // If a user message is first at head of the chat history, it's
  // already displayed with the session so we should ignore it.
  // This happens post an agent session when spinning a new chat.
  if (preprocessedLogEntries.length > 0 && chatMessageHistory[0]?.role === 'user') {
    chatMessageHistory = chatMessageHistory.slice(1)
  }

  if (chatMessageHistory.length === 0) {
    return preprocessedLogEntries
  }

  const alignOffset = findAlignmentOffset(preprocessedLogEntries, chatMessageHistory)

  if (alignOffset === null) {
    return [...preprocessedLogEntries, ...(chatMessageHistory as unknown as CustomAgentGroupLogEntry[])]
  }

  const head = preprocessedLogEntries.slice(0, alignOffset)
  const tail = preprocessedLogEntries.slice(alignOffset)
  const overlapLength = Math.max(tail.length, chatMessageHistory.length)
  const merged: CustomAgentGroupLogEntry[] = []

  for (let i = 0; i < overlapLength; i++) {
    const logEntry = tail[i]
    const chatEntry = chatMessageHistory[i]

    if (logEntry && chatEntry) {
      const logLen = getEntryContentLength(logEntry as LogEntry)
      const chatLen = getEntryContentLength(chatEntry)
      merged.push(chatLen > logLen ? (chatEntry as CustomAgentGroupLogEntry) : logEntry)
    } else if (logEntry) {
      merged.push(logEntry)
    } else if (chatEntry) {
      merged.push(chatEntry as CustomAgentGroupLogEntry)
    }
  }

  return head.concat(merged)
}
